import { SEO } from "@/components/SEO";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { HelpCircle, BookOpen, Mail, Clock, MessageSquare, AlertCircle } from "lucide-react";

const ContactPage = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();

  return (
    <>
      <SEO 
        title="Kontakta KlarFinans - Vi hjälper dig" 
        description="Har du frågor om privatlån, bolån eller företagslån? Kontakta KlarFinans på info@klarfinans.se. Vi svarar så snart vi kan."
        keywords={["Kontakt", "Kundservice", "Hjälp", "Support", "KlarFinans"]} 
      />
      
      <Layout>
        <div className="min-h-screen bg-gradient-to-b from-background via-muted/20 to-background">
          <section className="relative py-16 md:py-24 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-4xl mx-auto text-center space-y-10">
                <h1 className="text-3xl md:text-5xl font-bold">
                  Hur kan vi <span className="gradient-text">hjälpa dig?</span>
                </h1>
              </div>
            </div>
          </section>

          <section className="py-4 md:py-8">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto mb-8">
                <div className="grid md:grid-cols-2 gap-6">
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer group" onClick={() => navigate(`/${currentLanguage}/faq`)}>
                    <CardHeader>
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center shrink-0">
                          <HelpCircle className="h-6 w-6 text-success" />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="group-hover:text-primary transition-colors">Vanliga frågor</CardTitle>
                          <CardDescription className="mt-2">Svar på de mest förekommande frågorna om lån, ansökningar och återbetalning</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>

                  <Card className="hover:shadow-lg transition-shadow cursor-pointer group" onClick={() => navigate(`/${currentLanguage}/klarsprak`)}>
                    <CardHeader>
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <BookOpen className="h-6 w-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="group-hover:text-primary transition-colors">Klarspråk</CardTitle>
                          <CardDescription className="mt-2">Utforska våra pedagogiska guider som förklarar finansiella termer i lättförståelig text</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                </div>
              </div>

              <div className="max-w-4xl mx-auto mb-8">
                <Card className="border-primary/20">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <Mail className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <CardTitle>Kontakta oss via e-post</CardTitle>
                        <CardDescription className="mt-2">Har du frågor, feedback eller förslag? Vi finns här för att hjälpa dig!</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 text-lg">
                        <Mail className="h-5 w-5 text-primary" />
                        <a href="mailto:info@klarfinans.se" className="text-primary hover:underline font-medium">info@klarfinans.se</a>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span>Vi svarar vanligtvis inom 24-48 timmar</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="max-w-4xl mx-auto">
                <Card className="bg-muted/50">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                        <AlertCircle className="h-6 w-6 text-amber-500" />
                      </div>
                      <div className="flex-1">
                        <CardTitle>Vanliga orsaker till kontakt</CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <MessageSquare className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                        <span className="text-sm text-muted-foreground">Frågor om specifika låneprodukter eller sparalternativ</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <MessageSquare className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                        <span className="text-sm text-muted-foreground">Feedback om vår webbplats eller tjänster</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <MessageSquare className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                        <span className="text-sm text-muted-foreground">Samarbetsmöjligheter eller partnerskap</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <MessageSquare className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                        <span className="text-sm text-muted-foreground">Teknisk support eller rapportering av fel</span>
                      </li>
                    </ul>
                    <div className="mt-6 p-4 bg-background/50 rounded-lg border border-border/50">
                      <p className="text-sm text-muted-foreground italic">
                        <strong className="text-foreground">Observera:</strong> Vi är en jämförelsetjänst och kan inte direkt behandla låneansökningar. För specifika ansökningar, använd länkarna till respektive bank eller långivare på vår webbplats.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>
        </div>
      </Layout>
    </>
  );
};

export default ContactPage;
